import type { PlanTierKey } from '@repo/types'

/**
 * The canonical plan-comparison row set — ONE source, read by both surfaces that
 * describe what a tier includes (working-conventions §36):
 *
 *  - `BillingPlanChangePage/PlanComparisonMatrix` — the four-column grid.
 *  - `BillingOverview` "More details" — the SAME rows for the current tier only,
 *    one column.
 *
 * Why these rows and not `plan.features`: `features` is a free-text string array
 * authored in Stripe as `marketing_features`, with "Plus everything in <tier>"
 * inheritance. It reads fine as a bullet list for one plan and does not align
 * into a grid at all, and the two surfaces would drift the moment either was
 * edited. These rows do align, and they are the same rows in both places.
 *
 * Rows are keyed by TIER KEY (`plan.tier_key`), never by plan title. Titles are
 * Stripe-authored billing copy — the catalog held "Vera Enterprise" until it was
 * renamed "Vera Scale" (VRG-393), which is exactly the point — so a
 * title-keyed lookup silently missed on every tier and rendered every
 * differentiating row as not-included. See `resolvePlanTierKey`.
 *
 * Packaging logic (see context/decisions/0004-plan-packaging.md):
 * - Core features ship on EVERY plan ("see the whole product, then upgrade") — the
 *   ✓-everywhere reassurance row.
 * - Differentiation lives in usage/volume, analytics depth, white-label, and teams.
 * - Scan history is a CUSTOMER-VISIBILITY gate only (30d on Starter); Vera retains
 *   100% of scan data server-side on every tier.
 * - Benchmarking → Individual+; custom email domain (VERA-362) → Business+; remove
 *   "Powered by Vera" → the white-label tiers only (see WHITE_LABEL_TIER_KEYS).
 *
 * Included allotments are hardcoded pending real plan fields (TODO(VRG-185) for
 * queries; included_activations is already served from the catalog).
 */
export type PlanComparisonCellValue = string | boolean

export interface PlanComparisonRow {
  label: string
  /**
   * Glossary entry id. Preferred over `tooltip` whenever the row names a term of
   * art: the tooltip body is then read from `docs/glossary.json` and the reader
   * gets a link to the full definition (working-conventions §44). Set BOTH only
   * when the row needs local phrasing but should still reach the definition.
   */
  term?: string
  /** Optional info-tooltip for rows whose meaning isn't self-evident. */
  tooltip?: string
  /** Same value for every tier (e.g. the shared graduated activation schedule). */
  all?: PlanComparisonCellValue
  /** Per-tier value keyed by `plan.tier_key`; missing → not included. */
  byTier?: Partial<Record<PlanTierKey, PlanComparisonCellValue>>
}

export interface PlanComparisonSection {
  title: string
  rows: PlanComparisonRow[]
}

/** The lead reassurance row: the whole product ships on every plan. */
export const CORE_FEATURES_ROW: PlanComparisonRow = {
  label: 'All core features',
  tooltip:
    'Certificate creation, tag encoding, scan tracking, product catalog, and the brand studio — the full product.',
  all: true,
}

/** The section the data-driven activations row is injected into. */
export const USAGE_SECTION_TITLE = 'Usage & limits'

/**
 * Label for the "Included activations / mo" row. The VALUE is data-driven
 * (`plan.included_activations`, served from the catalog), so each surface
 * supplies it — the matrix per column, the overview for one tier — but the copy
 * lives here so the two can't diverge.
 *
 * The definition itself is not here either: it is the `included-activations`
 * glossary entry, which the Billing overview's "Plan included activations" stat
 * also reads. Same quantity, one sentence, two surfaces (§44) — the previous
 * hand-written `tooltip` was a second copy of that sentence waiting to drift.
 */
export const INCLUDED_ACTIVATIONS_ROW = {
  label: 'Included activations / mo',
  term: 'included-activations',
} as const

/**
 * Label for the "Team seats" row — VRG-438. Data-driven for the same reason as
 * activations above, and for a sharper one: until VRG-438 this row carried a
 * hardcoded `byTier` of 1 / 1 / 5 / Unlimited against an API that counted and
 * limited NOTHING. A number on a pricing page is a promise, and that one had no
 * implementation behind it — a Starter brand could be told it had one seat, invite
 * ten, and be charged for one.
 *
 * The value now comes from `plan.included_seats`, the same column the invite path
 * refuses against, so the matrix cannot advertise a figure the API does not honour.
 * `null` renders as "Unlimited", which is what null MEANS in that column.
 */
export const TEAM_SEATS_ROW = {
  label: 'Team seats',
  // A NUMBER a buyer compares plans on, with no explanation of what is being
  // counted. A seat is a person, not a role and not a login session, and that is
  // not derivable from "Team seats: 5" — hence the glossary term rather than a
  // retyped sentence (§44).
  term: 'seat',
} as const

/** How a seat allowance renders in the matrix. `null` = unlimited (see the column comment). */
export const formatSeatAllowanceCell = (seats: number | null | undefined): string =>
  seats == null ? 'Unlimited' : String(seats)

/**
 * TOOLTIPS ARE FOR ROWS WHOSE LABEL DOES NOT ALREADY SAY IT.
 *
 * There were 15, one on nearly every row, and at that density the info icons
 * stopped reading as "there is more here" and became a column of furniture
 * competing with the ticks and dashes that carry the actual comparison.
 *
 * Five were removed because their label was the whole explanation: ROI
 * dashboards, Download data, Remove "Powered by Vera", Custom roles, API
 * access. A tooltip that restates its label spends attention and returns
 * nothing.
 *
 * The ten kept each answer something the label cannot: what "core" covers,
 * what an AI query is and that it is metered, how "Graduated $25 -> $5" works,
 * what rollover means, why Scan history says "30 days" on one tier and "Full"
 * on the rest, that benchmarking is anonymised, which departments the advanced
 * roles cover, and what a custom sending domain replaces.
 *
 * Applies to BOTH surfaces -- these rows are shared with the billing
 * plan-change page, and the same reasoning holds there.
 */
export const PLAN_COMPARISON_SECTIONS: PlanComparisonSection[] = [
  {
    title: USAGE_SECTION_TITLE,
    rows: [
      // [activations row injected here at runtime]
        /*
          The 'Included AI queries / mo' row is REMOVED, not commented out, until
          VRG-20-AI.Queries.Billing.Analytics lands. It advertised 50/250/1,000/2,500
          per tier and NOTHING meters, counts or charges an AI query — there is no
          overage price either. A number on a pricing page is a promise; this one had
          no implementation behind it. The `ai-query` glossary entry stays: it explains
          what Ask is and claims no metering, so it is the one place to change when
          billing arrives.
        */
      // [seats row injected here at runtime — see TEAM_SEATS_ROW below]
      {
        // The rate ladder is a term of art with a glossary entry, so the row
        // reads it rather than carrying its own sentence (§44). The string this
        // replaced hardcoded "$25 down to $5" — the same two endpoints the `all`
        // value states — in a second place a re-pricing would have to find.
        label: 'Activation pricing',
        term: 'graduated-activation-rate',
        all: 'Graduated $25 → $5',
      },
      {
        // Likewise: `rollover` is defined once, and its entry says more than the
        // retyped line did (the balance carries forward and does not expire
        // while you stay subscribed), which is the part a reader weighing plans
        // actually wants.
        label: 'Uncapped activation rollover',
        term: 'rollover',
        all: true,
      },
    ],
  },
  {
    title: 'Analytics & data',
    rows: [
      // General analytics ships on every plan; the DEPTH (history window, ROI
      // dashboards, benchmarking, export) is the upgrade.
      { label: 'General analytics dashboard', all: true },
      {
        label: 'Scan history',
        tooltip:
          'How far back you can browse scan events in your dashboard. Vera retains ALL scan data server-side regardless of plan — this only sets the visible window.',
        // Customer-visible window only — Vera retains all scan data server-side.
        byTier: {
          starter: '30 days',
          individual: 'Full',
          business: 'Full',
          scale: 'Full',
        },
      },
      {
        label: 'ROI dashboards',
        byTier: { individual: true, business: true, scale: true },
      },
      {
        label: 'Category benchmarking',
        tooltip:
          'See how your metrics compare against anonymized brands in your category.',
        byTier: { individual: true, business: true, scale: true },
      },
      {
        label: 'Download data',
        byTier: { individual: true, business: true, scale: true },
      },
      {
        label: 'Advanced / custom analytics',
        tooltip:
          'Custom cohorts, segments, and bespoke analytics beyond the standard dashboards.',
        byTier: { scale: true },
      },
    ],
  },
  {
    title: 'White-label & branding',
    rows: [
      {
        // VERA-362 — brand-level sending domain. Gated Business+.
        label: 'Custom email sending domain',
        tooltip:
          "Send Vera's transactional emails from your own domain (e.g. hello@yourbrand.com) instead of no-reply@vera.inc.",
        byTier: { business: true, scale: true },
      },
      {
        // The "Powered by Vera" badge stays on every other tier (acquisition
        // channel); only the white-label tiers can toggle it off (WHITE_LABEL_TIER_KEYS).
        // VRG-487, 2026-08-30: the same toggle now also governs SMS — off tiers
        // get "VERA: {your name}" as the message prefix, on tiers get just
        // "{your name}" (notification.sendgrid.service.ts#buildSmsSenderPrefix).
        label: 'Remove "Powered by Vera"',
        tooltip:
          'Removes Vera’s attribution from your public certificate pages, email footers, and the front of every SMS.',
        byTier: { scale: true },
      },
    ],
  },
  {
    title: 'Team & access',
    rows: [
      {
        label: 'Advanced roles by department',
        tooltip:
          'Department-scoped roles (Finance, Design, Marketing, …) beyond the base Admin/Member roles.',
        byTier: { business: true, scale: true },
      },
      {
        label: 'Dedicated account manager',
        byTier: { business: true, scale: true },
      },
      {
        label: 'Custom roles',
        byTier: { scale: true },
      },
      {
        label: 'API access',
        byTier: { scale: true },
      },
    ],
  },
]

/** The shape both surfaces need from a plan to resolve its tier. */
export interface PlanTierSource {
  tier_key?: string | null
  title?: string | null
}

/**
 * The plan's tier key, for looking up `byTier` values.
 *
 * TRANSITIONAL: the title-derived fallback exists only so an un-migrated database
 * (rows written before `tier_key` landed, or before `sync-stripe-catalog.ts` has
 * been re-run against it) does not regress to the all-dashes matrix this column
 * was added to fix. It derives the key the same way the 0081 backfill does —
 * strip a leading "Vera ", lowercase. Delete it once every environment has run
 * the sync; it is a bug-compatible crutch, not a supported second source.
 */
export const resolvePlanTierKey = (
  plan: PlanTierSource | null | undefined,
): string | null => {
  if (!plan) return null
  if (plan.tier_key) return plan.tier_key
  if (!plan.title) return null
  return plan.title.replace(/^Vera\s+/i, '').trim().toLowerCase() || null
}

/**
 * The one-line positioning copy under a plan's price — REPO-OWNED, keyed by tier.
 *
 * This used to be whatever Stripe's `product.description` said: the seeder writes
 * it at `createProduct`, `sync-stripe-catalog` mirrors it into
 * `stripe_subscription_products.description`, and both the matrix and the signup
 * payment summary rendered it verbatim. That made a piece of MARKETING COPY
 * unreviewable, undiffable and editable by anyone with a Stripe dashboard login,
 * and it had already drifted in two places at once (measured against
 * `GET /signup/plans`, 2026-08-27):
 *
 *   starter — Stripe "For getting started — pay only when you sell";
 *             `seed-stripe-catalog.ts` "For getting started"
 *   scale   — Stripe "For high-volume brands and large catalogues";
 *             the seeder's stale top-tier entry "For large organizations"
 *
 * The seeder cannot heal that on its own: `tier.description` is only ever passed
 * at `createProduct`, and on reuse `products.update` writes metadata only — with
 * a deliberate drift GATE around it, because a blind write once reset Starter's
 * included activations from 3 to 1. So "fix it in Stripe" is an external
 * mutation, per-account (test and prod are separate), and would still leave the
 * two surfaces unable to guarantee they agree.
 *
 * Owning it here instead gives what was actually asked for: ONE string, rendered
 * by every surface, changed in a reviewed diff. Stripe's own `description` keeps
 * its own job — it is billing-document copy for Stripe-hosted surfaces — and is
 * left untouched, so nothing on an invoice or in the dashboard changes.
 *
 * Values are the LIVE catalog's, verbatim, except `starter` — the one line
 * VERA-1095 changes. `enterprise` is the contact-sales tier: it has no Stripe
 * price and therefore never appears as a column today, but `EnterpriseContactCard`
 * describes it and the key exists in `PLAN_TIER_KEYS`, so it is stated rather
 * than left to a fallback that would resolve to nothing.
 */
/**
 * A tier key → its display NAME. Repo-owned, for the same reason
 * `PLAN_TIER_DESCRIPTIONS` below is.
 *
 * ── Why this is not "just capitalise the key" ────────────────────────────────
 *
 * Because a key and its title are allowed to diverge, and have. `enterprise` was
 * renamed to `scale` in Stripe in 2026-08 (VRG-393) when "Enterprise" was needed
 * for the unpriced contact-sales tier; a `charAt(0).toUpperCase()` would have
 * gone on rendering a confident, wrong title through that change with nothing to
 * notice. The keys happen to Title Case correctly TODAY, which is exactly what
 * makes the shortcut tempting and what makes it fail silently later.
 *
 * ── Why not read `plan.title` off the catalog ────────────────────────────────
 *
 * A title from Stripe is billing-document copy — it reads "Vera Scale", it
 * changes for invoice reasons, and it is only available where a PLAN object is
 * in hand. The live consumer is the signups grid's `planInterest` column, which
 * has a tier SLUG on a lead who has no subscription and may never have one, so
 * there is no plan to read a title from. Hence: keyed by tier, owned here.
 *
 * ── TOTAL, not `Partial` — and that is the mechanism ─────────────────────────
 *
 * `Record<PlanTierKey, string>` makes a tier added to `PLAN_TIER_KEYS` a COMPILE
 * ERROR in this file rather than a lookup that silently falls through to a raw
 * slug on a screen. Deliberately stricter than `PLAN_TIER_DESCRIPTIONS`, which is
 * `Partial` with a documented Stripe fallback; there is no fallback here, so the
 * type is the only thing standing in for one.
 *
 * ⚠️ `enterprise` HAS NO STRIPE PRICE. It is the contact-sales tier and appears
 * as no plan column anywhere — it is titled here because a lead can express
 * interest in it, which is a different thing from being able to buy it.
 */
export const PLAN_TIER_TITLES: Record<PlanTierKey, string> = {
  starter: 'Starter',
  individual: 'Individual',
  business: 'Business',
  scale: 'Scale',
  enterprise: 'Enterprise',
}

export const PLAN_TIER_DESCRIPTIONS: Partial<Record<PlanTierKey, string>> = {
  starter: 'For getting started',
  individual: 'For solo brands and creators',
  business: 'For growing teams',
  scale: 'For high-volume brands and large catalogues',
  enterprise: 'For large organizations',
}

/**
 * A plan's display description: the repo-owned line for its tier, falling back
 * to whatever the catalog mirrored out of Stripe.
 *
 * The fallback is deliberate and narrow. A tier created in the Stripe dashboard
 * before this file knows about it still renders a description instead of a blank
 * column — a missing entry degrades to the old behaviour rather than to nothing.
 * It is not a second source: every tier in `PLAN_TIER_KEYS` has an entry above,
 * so the fallback is unreachable for the catalog as it stands.
 *
 * Called at each DISPLAY site rather than at the two API boundaries (signup's
 * `toPlanColumn`, the authenticated `subscriptionApi`) on purpose. A boundary
 * that has to remember to resolve is the opt-in pattern §42 records 35 of 37
 * call sites forgetting; resolving where the string is rendered means a new
 * surface reading `plan.description` directly is the only way to reintroduce
 * the drift, and there are two such surfaces, both of which call this.
 */
export const resolvePlanDescription = (
  plan: (PlanTierSource & { description?: string | null }) | null | undefined,
): string | null => {
  const tierKey = resolvePlanTierKey(plan)
  const owned = tierKey
    ? PLAN_TIER_DESCRIPTIONS[tierKey as PlanTierKey]
    : undefined
  return owned ?? plan?.description ?? null
}

/** A row's value for one tier. Missing → not included. */
export const resolvePlanComparisonCell = (
  row: PlanComparisonRow,
  tierKey: string | null,
): PlanComparisonCellValue => {
  if (row.all !== undefined) return row.all
  if (!tierKey) return false
  return row.byTier?.[tierKey as PlanTierKey] ?? false
}

export interface PlanHighlight {
  label: string
  /** Glossary entry id — see `PlanComparisonRow.term`. */
  term?: string
  tooltip?: string
  /** `true` → the label alone is the feature; a string → "label: value". */
  value: PlanComparisonCellValue
}

export interface PlanHighlightSection {
  title: string
  items: PlanHighlight[]
}

/**
 * The tiers the row set actually describes, derived from the rows themselves so
 * there is no second list to fall out of sync with them.
 */
const DESCRIBED_TIER_KEYS = new Set<string>(
  PLAN_COMPARISON_SECTIONS.flatMap((section) =>
    section.rows.flatMap((row) => Object.keys(row.byTier ?? {})),
  ),
)

/**
 * Whether the canonical rows can describe this tier at all.
 *
 * A tier the row set does not know still matches every `all:` row, so it would
 * render a handful of universal rows and silently omit everything that makes it
 * different — worse than saying nothing. Callers use this to fall back to the
 * plan's own free-text `features` instead.
 */
export const hasPlanHighlights = (tierKey: string | null): boolean =>
  !!tierKey && DESCRIBED_TIER_KEYS.has(tierKey)

/**
 * The canonical rows for ONE tier, for the single-column "More details" list:
 * the same rows the matrix renders, minus every row this tier does not get (a
 * feature you do NOT have is not a detail of your plan). Sections that end up
 * empty are dropped, and an unrecognised tier yields nothing at all (see
 * `hasPlanHighlights`). `CORE_FEATURES_ROW` is deliberately NOT included — both
 * surfaces render it as a lead row above the sections.
 */
export const planHighlightSections = (
  tierKey: string | null,
  includedActivations?: number | null,
  /**
   * VRG-438. Distinguished from "not supplied" by `undefined`, because `null` is a
   * MEANINGFUL value here (unlimited) and would otherwise be indistinguishable from
   * a caller that does not know the seat count.
   */
  includedSeats?: number | null,
  hasSeatData = includedSeats !== undefined,
): PlanHighlightSection[] =>
  !hasPlanHighlights(tierKey)
    ? []
    : PLAN_COMPARISON_SECTIONS.map((section) => {
        const rows =
          section.title === USAGE_SECTION_TITLE
            ? [
                ...(includedActivations != null
                  ? [
                      {
                        ...INCLUDED_ACTIVATIONS_ROW,
                        all: String(includedActivations),
                      } as PlanComparisonRow,
                    ]
                  : []),
                ...(hasSeatData
                  ? [
                      {
                        ...TEAM_SEATS_ROW,
                        all: formatSeatAllowanceCell(includedSeats),
                      } as PlanComparisonRow,
                    ]
                  : []),
                ...section.rows,
              ]
            : section.rows

        const items = rows
          .map((row) => ({
            label: row.label,
            term: row.term,
            tooltip: row.tooltip,
            value: resolvePlanComparisonCell(row, tierKey),
          }))
          .filter((item) => item.value !== false)

        return { title: section.title, items }
      }).filter((section) => section.items.length > 0)
